import type { ChatMessage } from '@bosmart/types'
import { triggerInterviewProcessing } from '@/lib/api-client'
import { logger } from '@/lib/logging'
import { buildTranscriptSegments, formatTranscriptionForUpload } from './format'
import type { SessionRefs } from './types'

interface FinalizeRecordingArgs {
  refs: SessionRefs
  history: ChatMessage[]
  duration: number
  interviewId: string
  token?: string
  showNotification: (message: string) => void
}

export async function finalizeRecording({
  refs,
  history,
  duration,
  interviewId,
  token,
  showNotification,
}: FinalizeRecordingArgs) {
  if (!refs.recordingManagerRef.current) {
    logger.warn('No recording manager found')
    return
  }

  try {
    logger.info('Stopping recording and finalizing multipart upload metadata')
    const result = await refs.recordingManagerRef.current.stopRecording()
    const { uploadMetadata, recordingMetadata } = result

    if (!uploadMetadata || !recordingMetadata) {
      logger.warn('No multipart upload metadata available for background processing', {
        interviewId,
        hasUploadMetadata: !!uploadMetadata,
        hasRecordingMetadata: !!recordingMetadata,
      })
      showNotification('Warning: Recording may not have been saved')
      return
    }

    logger.info('Multipart upload ready, triggering background processing', {
      interviewId,
      uploadId: uploadMetadata.uploadId,
      partsCount: uploadMetadata.parts.length,
      duration: recordingMetadata.duration,
    })

    await triggerInterviewProcessing(interviewId, {
      uploadMetadata,
      recordingMetadata: {
        startTime: recordingMetadata.startTime.toISOString(),
        endTime: recordingMetadata.endTime.toISOString(),
        duration: recordingMetadata.duration,
        mimeType: recordingMetadata.mimeType,
      },
      transcription: {
        text: formatTranscriptionForUpload(history),
        segments: buildTranscriptSegments(history),
      },
      duration,
      token,
    })

    logger.info('Background processing job queued successfully', { interviewId })
  } catch (err) {
    logger.error('Failed to finalize recording metadata', { error: String(err) })
    showNotification('Warning: Background processing may have failed - check with admin')
  } finally {
    refs.recordingManagerRef.current?.cleanup()
    refs.recordingManagerRef.current = null
  }
}
